import type { AxiosError, AxiosInstance } from 'axios';
import { openTerritoryGateImperative } from '@/store/territoryGateStore';
import type { TerritoryBlockedBody } from './types';

/**
 * Backstop for the server-side TerritoryGuard / category enforcer. If a gated
 * action returns 403 `{ code: 'TERRITORY_BLOCKED', ... }`, surface the block
 * screen. Unlike KYC there's no replay — a territory block can't be cleared in
 * session — so the original 403 always propagates after opening the screen.
 *
 * Returns a teardown function (tests / HMR).
 */
export function installTerritoryInterceptor(instance: AxiosInstance): () => void {
  const id = instance.interceptors.response.use(
    (response) => response,
    // Async so a synchronous throw surfaces as a rejected promise to the caller.
    async (error: AxiosError) => {
      const body = error.response?.data as TerritoryBlockedBody | undefined;
      if (error.response?.status === 403 && body?.code === 'TERRITORY_BLOCKED') {
        openTerritoryGateImperative({ reason: body.reason ?? null, category: body.category ?? null });
      }
      throw error;
    },
  );
  return () => instance.interceptors.response.eject(id);
}
